/**
 * Document identity and save-state model.
 *
 * A document's *content* lives in the editor layer; this module only knows the
 * metadata needed to name it, place it in a tab and describe — truthfully —
 * where its words currently live.
 */

export type DocKind = 'md' | 'txt';

/**
 * Where a document came from, which decides what "Save" can mean:
 * - `new`: created in MyTXT; it has never been written to a file.
 * - `file`: linked to a file MyTXT may write in place (File System Access or a desktop shell).
 * - `import`: read from a file without write access; saving produces a downloaded copy.
 */
export type DocOrigin = 'new' | 'file' | 'import';

export type LineEnding = '\n' | '\r\n';

export interface TextFormat {
  eol: LineEnding;
  bom: boolean;
}

/**
 * What the cloud is known to hold for a document: the last revision the server
 * acknowledged, and the content and metadata at that revision. The text itself
 * lives in the editor layer; only its fingerprint is kept here. A link never
 * holds a file handle or path — those stay on the device.
 *
 * `revision: 0` means "meant for the cloud, not created there yet".
 */
export interface CloudLink {
  /** The account whose library this document belongs to. */
  account: string;
  revision: number;
  /** `contentHash` of the text at `revision`. */
  hash: string;
  name: string;
  kind: DocKind;
  format: TextFormat;
  workspaceId: string | null;
}

export interface DocMeta {
  id: string;
  /** Full file name, including extension. */
  name: string;
  kind: DocKind;
  origin: DocOrigin;
  /** True when the text differs from what was last read from or written to a file. */
  dirty: boolean;
  /** Fingerprint of the text as last read from or written to a file (or `''` for new documents). */
  savedHash: string;
  /** How the current clean state was reached, for honest status copy. */
  lastSave: 'none' | 'file' | 'download';
  format: TextFormat;
  /** `lastModified` of the linked file when MyTXT last read or wrote it. */
  fileModified?: number;
  /** Present when the document belongs to a cloud library. Independent of `dirty`, which is about files. */
  cloud?: CloudLink;
  /** The cloud does not yet hold the current text or name (meaningful only with `cloud`). */
  unsynced?: boolean;
  /**
   * The logical workspace this document belongs to (cloud documents). Changed only by
   * deliberate acts — creating or moving a document — never by opening or closing windows.
   * Absent: whatever the cloud last recorded.
   */
  workspaceId?: string | null;
}

export type SaveStatus =
  | 'blank'
  | 'draft'
  | 'edited'
  | 'saved'
  | 'unchanged'
  | 'downloaded'
  /** A cloud document whose latest text the cloud acknowledged. */
  | 'cloud'
  /** A cloud document with words the cloud does not hold yet. */
  | 'cloud-pending';

export const DEFAULT_FORMAT: TextFormat = { eol: '\n', bom: false };

const MARKDOWN_EXTENSIONS = ['md', 'markdown', 'mdown', 'mkd'];

export function extensionFor(kind: DocKind): string {
  return kind === 'md' ? '.md' : '.txt';
}

/** Split a file name into its base and extension (the extension keeps its dot). */
export function splitName(name: string): { base: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot === name.length - 1) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot) };
}

export function kindFromName(name: string): DocKind {
  const ext = splitName(name).ext.slice(1).toLowerCase();
  return MARKDOWN_EXTENSIONS.includes(ext) ? 'md' : 'txt';
}

/** Remove characters that cannot appear in a file name on macOS or Windows. */
export function sanitizeBaseName(input: string): string {
  return input
    .replace(/[\\/:*?"<>|\p{Cc}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, 120);
}

/**
 * "Untitled", then "Untitled 2", "Untitled 3"… — the lowest number not used
 * by an open document, regardless of extension, so names never collide.
 */
export function nextUntitledName(existing: readonly string[], kind: DocKind): string {
  const taken = new Set<number>();
  for (const name of existing) {
    const match = /^Untitled(?: (\d+))?$/.exec(splitName(name).base);
    if (match) taken.add(match[1] ? Number(match[1]) : 1);
  }
  let n = 1;
  while (taken.has(n)) n++;
  return (n === 1 ? 'Untitled' : `Untitled ${n}`) + extensionFor(kind);
}

/** Rename keeping the document's extension; returns null for an unusable name. */
export function renamedFile(base: string, kind: DocKind): string | null {
  const clean = sanitizeBaseName(base);
  if (!clean) return null;
  // Accept a typed extension that matches the kind ("notes.md") rather than doubling it.
  const typed = splitName(clean);
  const ext = typed.ext.slice(1).toLowerCase();
  const own = kind === 'md' ? MARKDOWN_EXTENSIONS.includes(ext) : ext === 'txt' || ext === 'text';
  const stem = own ? typed.base : clean;
  return stem + extensionFor(kind);
}

export function saveStatus(doc: Pick<DocMeta, 'origin' | 'dirty' | 'lastSave' | 'cloud' | 'unsynced'>): SaveStatus {
  // A cloud document's home is the cloud; "has it been saved to a file" is a separate, optional question.
  if (doc.cloud && doc.origin !== 'file') return doc.unsynced ? 'cloud-pending' : 'cloud';
  if (doc.dirty) return doc.origin === 'new' && doc.lastSave === 'none' ? 'draft' : 'edited';
  if (doc.lastSave === 'download') return 'downloaded';
  if (doc.origin === 'file') return 'saved';
  if (doc.origin === 'import') return 'unchanged';
  return 'blank';
}

/** True when closing the document would lose words that exist nowhere else. */
export function needsSaveBeforeClose(doc: DocMeta): boolean {
  return doc.dirty;
}

/**
 * Display labels for tabs. Identical names get a quiet ordinal so two
 * `notes.md` files opened from different folders stay distinguishable.
 */
export function displayLabels(docs: readonly Pick<DocMeta, 'id' | 'name'>[]): Map<string, string | null> {
  const seen = new Map<string, number>();
  const result = new Map<string, string | null>();
  for (const doc of docs) {
    const key = doc.name.toLowerCase();
    const count = (seen.get(key) ?? 0) + 1;
    seen.set(key, count);
    result.set(doc.id, count > 1 ? String(count) : null);
  }
  return result;
}

/** FNV-1a fingerprint plus length; cheap enough to run on idle after edits. */
export function textHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return `${(h >>> 0).toString(36)}.${text.length.toString(36)}`;
}

/**
 * A 106-bit fingerprint plus length (two independent 53-bit lanes). Used to
 * decide whether the cloud already holds exactly this text, where a collision
 * would mean an edit is silently not uploaded — so it is far stronger than
 * `textHash`, which only gates the file-dirty indicator.
 */
export function contentHash(text: string): string {
  let a1 = 0xdeadbeef;
  let a2 = 0x41c6ce57;
  let b1 = 0x9e3779b9;
  let b2 = 0x85ebca6b;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    a1 = Math.imul(a1 ^ c, 2654435761);
    a2 = Math.imul(a2 ^ c, 1597334677);
    b1 = Math.imul(b1 ^ c, 3266489917);
    b2 = Math.imul(b2 ^ c, 668265263);
  }
  const mix = (h1: number, h2: number) => {
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  };
  return `${mix(a1, a2).toString(36)}.${mix(b1, b2).toString(36)}.${text.length.toString(36)}`;
}

export const EMPTY_HASH = textHash('');

export function countWords(text: string): number {
  const matches = text.match(/[\p{L}\p{N}][\p{L}\p{N}'’_-]*/gu);
  return matches ? matches.length : 0;
}
